import React from 'react';
import PropTypes from 'prop-types';
import dayjs from 'helpers/dayjs';
import {
  MdBlock, MdLockOpen, MdLan, MdPublic, MdWarningAmber, MdSearch, MdClose, MdAlternateEmail,
} from 'react-icons/md';
import { Card, Button, Pagination, confirmAlert } from 'components/common';
import { escapeHtml } from 'helpers/utils';

const RULE_LABELS = {
  'login/ip/burst': 'Too many attempts in a few seconds',
  'login/ip/hourly': 'Too many attempts this hour',
  'login/email': 'Too many attempts on one account',
  'password_reset/ip': 'Too many password resets',
};

const SORTS = [
  // The question people open this panel with.
  { value: 'recent', label: 'Most recent' },
  // A different question: who is being hammered hardest, which is what an attack
  // looks like as opposed to somebody mistyping their password.
  { value: 'hits', label: 'Most hits' },
  { value: 'key', label: 'IP / account' },
];

const NETWORKS = [
  { value: '', label: 'Any network' },
  { value: 'internal', label: 'Office network' },
  { value: 'external', label: 'Outside' },
];

const controlClass =
  'tw-h-8 tw-px-2 tw-text-sm tw-rounded-md tw-border tw-border-solid tw-border-slate-200 tw-text-slate-600 focus:tw-outline-none focus:tw-border-primary-400';

/**
 * Who the sign-in rate limits are currently blocking, and the button to release
 * them.
 *
 * Rack::Attack keeps counters but cannot be asked "who is blocked right now" —
 * its keys are time-windowed and the cache cannot be listed. Without this panel
 * the only way to learn somebody was locked out of the portal was for them to
 * telephone and say so, and the only way to release them early was a shell.
 */
const ThrottledPanel = ({ throttles, filters, onFiltersChange, onClear }) => {
  const blocked = throttles?.blocked || [];
  const total = throttles?.total || 0;
  const hasFilters = Boolean(filters.q || filters.network || filters.rule);
  const limits = throttles?.limits;
  const idle = total === 0 && !hasFilters;

  // Always rendered, including when nobody is blocked.
  //
  // It used to hide itself when empty, on the theory that a permanently empty
  // panel gets ignored. That was wrong for a security control: with the panel
  // gone there is no way to tell "nobody is blocked" apart from "this stopped
  // working", and no way to reach the filters to go looking. It stays, but goes
  // quiet — no toolbar, no badge, and a line stating the limits in force.

  const handleClear = (row) => {
    const key = escapeHtml(row.key);

    confirmAlert({
      title: 'Lift the rate limit?',
      // Says what happens if they simply wait, because that is usually the right
      // answer and this button overrides a security control.
      message: `<b>${key}</b> will be able to sign in again immediately.<br/><br/>These limits clear themselves — within 20 seconds for a burst, within the hour otherwise — so lift one early only if waiting is not acceptable. The action is recorded against your name.`,
      type: 'warning',
      onConfirm: () => onClear(row.key),
    });
  };

  const update = (patch) => onFiltersChange({ ...filters, page: 1, ...patch });

  return (
    <Card
      className="tw-mb-4"
      padding="none"
      header={(
        <div className="tw-flex tw-items-center tw-gap-2">
          {/* Amber is a claim that something needs attention. When nothing is
              blocked it would be crying wolf every time the page loads. */}
          <MdBlock size={16} className={idle ? 'tw-text-slate-400' : 'tw-text-amber-600'} />
          <h6 className="tw-mb-0 tw-font-semibold tw-text-slate-700">Blocked by rate limiting</h6>
          {total > 0 && (
            <span className="tw-inline-flex tw-items-center tw-justify-center tw-h-5 tw-min-w-[20px] tw-px-1.5 tw-rounded-xl tw-bg-amber-500 tw-text-white tw-text-[11px] tw-font-bold">
              {total}
            </span>
          )}
        </div>
      )}
    >
      {/* The switch being off is itself worth shouting about: it is meant to be a
          temporary escape hatch, and one left off silently is a protection
          nobody knows is missing. */}
      {throttles?.enabled === false && (
        <div className="tw-flex tw-items-start tw-gap-2 tw-px-4 tw-py-2.5 tw-bg-red-50 tw-border-b tw-border-solid tw-border-red-200">
          <MdWarningAmber size={16} className="tw-text-red-500 tw-shrink-0 tw-mt-0.5" />
          <p className="tw-text-xs tw-text-red-700 tw-m-0">
            <strong>Sign-in rate limiting is switched off.</strong> Nothing is slowing
            down password guessing. Turn it back on in App Settings
            (<code className="tw-font-mono">RATE_LIMIT_ENABLED</code>) once whatever
            required this is resolved.
          </p>
        </div>
      )}

      {/* No point offering sort and filter controls over an empty list — they
          would only be a row of dead widgets. They come back the moment there
          is anything to narrow down. */}
      {!idle && (
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-px-4 tw-py-2.5 tw-border-b tw-border-solid tw-border-slate-100">
        <div className="tw-relative tw-flex tw-items-center">
          <MdSearch size={15} className="tw-absolute tw-left-2.5 tw-text-slate-400 tw-pointer-events-none" />
          <input
            type="text"
            value={filters.q}
            onChange={(event) => update({ q: event.target.value })}
            placeholder="IP or email…"
            className="tw-h-8 tw-pl-8 tw-pr-7 tw-w-48 tw-text-sm tw-rounded-md tw-border tw-border-solid tw-border-slate-200 focus:tw-border-primary-400 focus:tw-outline-none"
          />
          {filters.q && (
            <button
              type="button"
              onClick={() => update({ q: '' })}
              title="Clear search"
              className="tw-absolute tw-right-1.5 tw-text-slate-400 hover:tw-text-slate-700 tw-bg-transparent tw-border-0 tw-p-0.5"
            >
              <MdClose size={14} />
            </button>
          )}
        </div>

        <select value={filters.network} onChange={(e) => update({ network: e.target.value })} className={controlClass}>
          {NETWORKS.map((option) => (
            <option key={option.value || 'any'} value={option.value}>{option.label}</option>
          ))}
        </select>

        {/* Only the rules actually present, so the filter never offers a value
            that would return nothing. */}
        <select value={filters.rule} onChange={(e) => update({ rule: e.target.value })} className={controlClass}>
          <option value="">Any reason</option>
          {(throttles?.rules || []).map((rule) => (
            <option key={rule} value={rule}>{RULE_LABELS[rule] || rule}</option>
          ))}
        </select>

        <select
          value={filters.sort}
          onChange={(e) => update({ sort: e.target.value })}
          className={`${controlClass} tw-ml-auto`}
        >
          {SORTS.map((option) => (
            <option key={option.value} value={option.value}>Sort: {option.label}</option>
          ))}
        </select>
      </div>
      )}

      {blocked.length === 0 ? (
        <div className="tw-px-4 tw-py-4">
          <p className="tw-text-sm tw-text-slate-500 tw-m-0">
            {hasFilters ? 'Nothing matches these filters.' : 'Nobody is currently blocked.'}
          </p>
          {/* States what is actually protecting the sign-in page. Without it an
              empty panel reads as "no rate limiting here" — the opposite of the
              truth. */}
          {idle && limits && (
            <p className="tw-text-xs tw-text-slate-400 tw-m-0 tw-mt-1.5">
              Sign-in attempts are limited to{' '}
              <strong className="tw-font-semibold tw-text-slate-500">
                {limits.external_burst} per 20 seconds
              </strong>{' '}
              and {limits.external_hourly} per hour from an IP address, relaxed to{' '}
              {limits.internal_burst} and {limits.internal_hourly} on the office network.
              Anyone who trips a limit appears here, and can be released early.
            </p>
          )}
        </div>
      ) : (
        <div className="tw-divide-y tw-divide-slate-100">
          {blocked.map((row) => (
            <div key={`${row.key}-${row.rule}`} className="tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-2.5">
              {/* An email throttle keys on an address, an IP throttle on an
                  address — the icon has to say which, or a mailbox would be
                  labelled as being on the office network. */}
              {row.kind === 'account'
                ? <MdAlternateEmail size={15} className="tw-text-slate-400 tw-shrink-0" />
                : row.internal
                  ? <MdLan size={15} className="tw-text-slate-400 tw-shrink-0" />
                  : <MdPublic size={15} className="tw-text-slate-400 tw-shrink-0" />}

              <div className="tw-min-w-0 tw-flex-1">
                <p className="tw-m-0 tw-text-sm tw-font-mono tw-text-slate-700 tw-truncate">
                  {row.key}
                  {row.internal && (
                    <span className="tw-ml-2 tw-font-sans tw-text-[10px] tw-font-semibold tw-uppercase tw-text-slate-400">
                      office
                    </span>
                  )}
                </p>
                <p className="tw-m-0 tw-text-xs tw-text-slate-500">
                  {RULE_LABELS[row.rule] || row.rule}
                  {' · '}
                  <span className={row.hits > 10 ? 'tw-font-semibold tw-text-amber-700' : ''}>
                    {row.hits} {row.hits === 1 ? 'hit' : 'hits'}
                  </span>
                  {row.last_at && ` · last ${dayjs(row.last_at).fromNow()}`}
                </p>
              </div>

              {throttles?.can_manage && (
                <Button size="sm" variant="secondary" onClick={() => handleClear(row)}>
                  <MdLockOpen size={14} /> Unblock
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {total > (throttles?.per_page || 10) && (
        <div className="tw-px-4 tw-py-3 tw-border-t tw-border-solid tw-border-slate-100">
          <Pagination
            currentPage={throttles.page}
            totalPages={Math.ceil(total / throttles.per_page)}
            totalResults={total}
            perPage={throttles.per_page}
            onPageChange={(next) => onFiltersChange({ ...filters, page: next })}
          />
        </div>
      )}
    </Card>
  );
};

ThrottledPanel.propTypes = {
  throttles: PropTypes.shape({
    enabled: PropTypes.bool,
    can_manage: PropTypes.bool,
    blocked: PropTypes.array,
    total: PropTypes.number,
    page: PropTypes.number,
    per_page: PropTypes.number,
    rules: PropTypes.array,
    limits: PropTypes.object,
  }),
  filters: PropTypes.shape({
    q: PropTypes.string,
    network: PropTypes.string,
    rule: PropTypes.string,
    sort: PropTypes.string,
    page: PropTypes.number,
  }).isRequired,
  onFiltersChange: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};

export default ThrottledPanel;
